import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { formatUyMinor } from "@/lib/formatUyMinor";
import { QuoteBreakdown } from "@/components/features/notarial-requests/quote-breakdown";
import type { QuoteLineItemView } from "@/services/quoteLineItems";

export interface QuoteMyBidCardProps {
  priceMinor: number;
  /** Their own breakdown. Null on a bid submitted before breakdowns existed. */
  lineItems: QuoteLineItemView[] | null;
  pricesIncludeIva: boolean;
  currency: string;
  estimatedDays: number | null;
  message: string | null;
  /** `submitted` | `awarded` | `rejected` — withdrawn rows are not rendered. */
  status: string;
  /**
   * Whether the consumer has PAID. False on a quote that has merely been
   * chosen — which is not the same thing and must not be announced as if it
   * were (spec award-commits-nothing-until-payment).
   */
  awardPaid?: boolean;
  /** Present only while the bid can still be pulled. */
  onWithdraw?: () => void;
  isWithdrawing?: boolean;
  /** Opens the full price breakdown. Always available — it only reads. */
  onViewBreakdown?: () => void;
  /**
   * Reopens the bid form, seeded from this quote. Omitted once the quoting
   * window has closed: the server refuses an update past the deadline, so
   * offering the form would be inviting someone to fill in a refusal (FM-5).
   */
  onEdit?: () => void;
  /**
   * What a closed quoting window means for a bid that is still live.
   *
   * TWO THINGS AT ONCE, and the first is the one the professional wants: the
   * bid can still WIN. Awarding stays legal for 48 hours after bidding
   * closes (awardDeadlineAt = quotingExpiresAt + the award grace), so "El
   * plazo cerró" next to a live bid must not read as "this is over".
   *
   * The second is why the withdraw button went: the server would still
   * accept a withdrawal, but submitOrUpdate refuses a new bid past the
   * deadline, so withdrawing is one-way. That is a deliberate client policy,
   * and a control that silently disappears reads as a bug.
   */
  closedWindowNote?: string;
  /** Discipline-aware copy lookup from useProfessionalRequestCopy. */
  tp: (notaryRelativePath: string) => string;
  tpo: (notaryRelativePath: string, options?: Record<string, unknown>) => string;
}

/**
 * The professional's own bid on an RFQ.
 *
 * Was a flat stack — price and ETA run together in one sentence, the message
 * loose beneath it, the status badge below that — so the two numbers the
 * professional came back to check were the least visible things on the card.
 * They are stats now, with the status in the header where a card's state
 * belongs, and the message in its own labelled block.
 */
export function QuoteMyBidCard({
  priceMinor,
  lineItems,
  pricesIncludeIva,
  currency,
  estimatedDays,
  message,
  status,
  awardPaid = false,
  onWithdraw,
  isWithdrawing = false,
  onViewBreakdown,
  onEdit,
  closedWindowNote,
  tp,
  tpo,
}: QuoteMyBidCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
        <CardTitle className="text-base">{tp("requestDetail.myQuoteTitle")}</CardTitle>
        {/**
          * "¡Ganaste esta cotización!" IS A PROMISE, and it was being made on
          * a choice nobody had paid for. An award lapses back to quoting if
          * the payment window passes, and the professional is never told it
          * did — they were never told they won — so the celebration has to
          * wait for the money. Until then the badge says what is actually
          * true: chosen, waiting on payment.
          */}
        <StatusBadge
          variant={status === "awarded" && awardPaid ? "success" : status === "awarded" ? "warning" : "info"}
        >
          {status === "awarded" && !awardPaid
            ? tp("requests.quoteMyStatus_awardedAwaitingPayment")
            : tp(`requests.quoteMyStatus_${status}`)}
        </StatusBadge>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* The two numbers being checked. text-xl rather than text-2xl so they
            read as the page's largest DATA without competing with its title. */}
        <div className="flex flex-wrap gap-x-10 gap-y-3">
          <div>
            <p className="text-xl font-semibold tabular-nums text-foreground">
              {formatUyMinor(priceMinor, currency)}
            </p>
            <p className="text-xs text-muted-foreground">
              {tp("requestDetail.myQuotePriceLabel")}
            </p>
          </div>
          {estimatedDays != null ? (
            <div>
              <p className="text-xl font-semibold tabular-nums text-foreground">
                {tpo("requestDetail.quoteEtaDays", { count: estimatedDays })}
              </p>
              <p className="text-xs text-muted-foreground">
                {tp("requestDetail.myQuoteEtaLabel")}
              </p>
            </div>
          ) : null}
        </div>

        {/* Divider supplied here, not by the component: this is the one place
            the breakdown follows a block of stats it must be separated from. */}
        {lineItems != null && lineItems.length > 0 ? (
          <div className="border-t border-border pt-3">
            <QuoteBreakdown
              lineItems={lineItems}
              currency={currency}
              pricesIncludeIva={pricesIncludeIva}
            />
          </div>
        ) : null}

        {message?.trim() ? (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              {tp("requestDetail.myQuoteMessageLabel")}
            </p>
            <p className="whitespace-pre-line break-words rounded-md bg-muted/40 px-3 py-2 text-sm text-foreground">
              {message.trim()}
            </p>
          </div>
        ) : null}

        {/* RANKED BY WHAT THEY DO, left to right: look, change, undo. Mobile
            stacks these full-width because it is a phone; here they sit in the
            row this portal already uses for a card's actions.

            The breakdown stays available even once the window has closed —
            it only reads, and "what was my client charged" is a question a
            professional asks most AFTER the bidding is over. */}
        {onViewBreakdown || onEdit || onWithdraw ? (
          <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
            {onViewBreakdown ? (
              <Button variant="ghost" size="sm" onClick={onViewBreakdown}>
                {tp("requestDetail.myQuoteViewBreakdown")}
              </Button>
            ) : null}
            {onEdit ? (
              <Button variant="outline" size="sm" disabled={isWithdrawing} onClick={onEdit}>
                {tp("requestDetail.myQuoteEditButton")}
              </Button>
            ) : null}
            {onWithdraw ? (
              <Button variant="outline" size="sm" disabled={isWithdrawing} onClick={onWithdraw}>
                {tp("requestDetail.quoteWithdrawButton")}
              </Button>
            ) : null}
          </div>
        ) : null}
        {!onWithdraw && closedWindowNote ? (
          <p className="border-t border-border pt-3 text-xs text-muted-foreground">
            {closedWindowNote}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
